import { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import { toast } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { btn } from "../../../components/Button/buttonStyles";
import LinkedInvoicesCard from "../../../components/Payments/LinkedInvoicesCard";
import PaymentMethodEditor from "../../../components/Payments/PaymentMethodEditor";
import { COVERAGE_CLS } from "../../../components/Payments/paymentParties";

const fmtISO = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const Field = ({ label, value, children }) => (
  <div className="flex items-start">
    <span className="w-52 text-sm font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-sm text-gray-900">{children ?? value ?? "—"}</span>
  </div>
);

const SectionHeader = ({ title }) => (
  <p className="text-base font-bold text-gray-900 pt-3 pb-1">{title}</p>
);

const Section = ({ title, count, actionBtn, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
      <span className="text-xl font-bold text-gray-900">{title}</span>
      {actionBtn}
    </div>
    {count !== undefined && (
      <p className="text-xs text-gray-500 px-5 pt-3">
        Total <span className="font-semibold">{count}</span>{" "}
        {count === 1 ? "item" : "items"}.
      </p>
    )}
    {children}
  </div>
);

const ClientPaymentDetailPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.CLIENT_PAYMENTS);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingMethod, setEditingMethod] = useState(false);

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const res = await api.get(`/project-finances/client-payments/${id}`, {
          withCredentials: true,
        });
        setPayment(res.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load payment details.");
      } finally {
        setLoading(false);
      }
    };
    fetchPayment();
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return <p className="text-center mt-10 text-danger-600 font-medium">{error}</p>;
  if (!payment)
    return <p className="text-center mt-10 text-gray-500">Payment not found.</p>;

  /* derived values */
  const getClientName = () => {
    const c = payment.client;
    if (!c) return "—";
    if (c.type?.toLowerCase() === "company") return c.company_name || "—";
    if (c.primary_user)
      return `${c.primary_user.first_name} ${c.primary_user.last_name}`.trim();
    return "—";
  };

  const currencyCode = payment.currency?.currency?.code || "—";
  const legalEntity = payment.client?.legal_entity || "—";
  const createdByName =
    payment.createdByAdmin?.details?.company_name ||
    payment.createdByAdmin?.username ||
    "—";

  /* payment method */
  const pm = payment.paymentMethod;
  const pmTypeFmt = pm?.payment_method?.replace(/_/g, " ") || "—";

  let pmName = "—";
  let pmDetails = "—";

  if (pm?.bank_transfer_detail) {
    const bt = pm.bank_transfer_detail;
    pmName = bt.payment_method_name || "—";
    const parts = [];
    if (bt.bank_name) parts.push(`Bank name: ${bt.bank_name}`);
    if (bt.bank_address) parts.push(`Bank address: ${bt.bank_address}`);
    if (bt.swift) parts.push(`SWIFT: ${bt.swift}`);
    if (bt.iban) parts.push(`IBAN: ${bt.iban}`);
    if (bt.ifsc_code) parts.push(`IFSC: ${bt.ifsc_code}`);
    if (bt.account_number) parts.push(`Account number: ${bt.account_number}`);
    if (bt.beneficiary_name) parts.push(`Account beneficiary name: ${bt.beneficiary_name}`);
    if (bt.beneficiary_address) parts.push(`Account beneficiary address: ${bt.beneficiary_address}`);
    if (bt.country) parts.push(`Country: ${bt.country}`);
    pmDetails = parts.join("\n") || "—";
  } else if (pm?.email_payment_detail) {
    const ep = pm.email_payment_detail;
    pmName = ep.account_holder_name || ep.email || "—";
    pmDetails = ep.email || "—";
  } else if (pm?.other_payment_detail) {
    pmName = pm.other_payment_detail.payment_method_name || "—";
    pmDetails = "—";
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">
            Payment {payment.payment_code}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.error("Download as PDF not yet implemented.")}
            className={btn("secondary", "sm")}
          >
            Download as PDF
          </button>
          {/* Payments are permanent, so there is no Delete. */}
          {!readOnly && (
            <button
              onClick={() => toast.error("Update not yet implemented.")}
              className={btn("primary", "sm")}
            >
              Update
            </button>
          )}
        </div>
      </div>

      {/* Main detail card */}
      <div className="bg-white shadow rounded-lg p-8 space-y-3 text-sm">
        <Field label="Client">
          {payment.client ? (
            <Link
              to={`/clients/${payment.client.id}`}
              className="text-brand-600 hover:text-brand-800 underline"
            >
              {getClientName()}
            </Link>
          ) : (
            "—"
          )}
        </Field>
        <Field label="Total amount">
          <strong>{fmtNum(payment.amount)}</strong>
        </Field>
        <Field label="Currency" value={currencyCode} />
        <Field label="Paid at" value={fmtISO(payment.paid_at)} />

        <SectionHeader title="Invoice coverage" />
        <Field label="Invoice coverage">
          <span className={COVERAGE_CLS[payment.coverage_by_invoices] ?? "text-gray-600"}>
            {payment.coverage_by_invoices || "Not covered"}
          </span>
        </Field>
        <Field label="Linked amount" value={fmtNum(payment.linked_amount)} />
        <Field label="Unlinked amount" value={fmtNum(payment.unlinked_amount)} />

        <SectionHeader title="Other details" />
        <Field label="Legal entity" value={legalEntity} />
        <Field label="Note">
          <span className="whitespace-pre-line">{payment.note || "—"}</span>
        </Field>
        <Field label="Created at" value={`${fmtISO(payment.createdAt)} | ${createdByName}`} />
      </div>

      {/* Payment Method */}
      <Section
        title="Payment Method"
        actionBtn={
          !readOnly && !editingMethod && (
            <button onClick={() => setEditingMethod(true)} className={btn("secondary", "sm")}>
              Change
            </button>
          )
        }
      >
        {editingMethod ? (
          <PaymentMethodEditor
            party="client"
            payment={payment}
            currentId={payment.payment_method_id}
            onCancel={() => setEditingMethod(false)}
            onSaved={(updated) => {
              setPayment(updated);
              setEditingMethod(false);
            }}
          />
        ) : (
          <div className="px-8 py-5 space-y-3">
            <Field label="Name" value={pmName} />
            <Field label="Type">
              <span className="capitalize">{pmTypeFmt}</span>
            </Field>
            <Field label="Details">
              <span className="whitespace-pre-line">{pmDetails}</span>
            </Field>
          </div>
        )}
      </Section>

      <LinkedInvoicesCard
        party="client"
        payment={payment}
        currency={payment.currency?.currency?.code}
        readOnly={readOnly}
        onChange={setPayment}
      />

      {/* Messages */}
      <Section title="Messages">
        <div className="px-5 py-4 space-y-3">
          <textarea
            rows={4}
            placeholder="Write a message..."
            className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button className={btn("primary", "sm")}>
            Send message to client
          </button>
        </div>
      </Section>
    </div>
  );
};

export default ClientPaymentDetailPage;
